import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { AlertTriangle, Loader2, Check, X, EyeOff, Flag, Calendar } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Report {
  id: string;
  reporter_user_id: string;
  report_reason: string;
  report_details: string | null;
  status: string;
  created_at: string;
}

interface ContentReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'event' | 'job';
  contentId: string;
  contentTitle: string;
  onAction: () => void;
}

const REPORT_REASONS: Record<string, string> = {
  hate_speech: "Incitamento all'odio",
  discrimination: "Discriminazione",
  fraud: "Frode / Truffa",
  inappropriate_content: "Contenuto inappropriato",
  fake_profile: "Profilo/Contenuto falso",
  other: "Altro",
};

export function ContentReportModal({
  isOpen,
  onClose,
  contentType,
  contentId,
  contentTitle,
  onAction,
}: ContentReportModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [hideDialogOpen, setHideDialogOpen] = useState(false);
  const [hideReason, setHideReason] = useState("");

  useEffect(() => {
    if (isOpen && contentId) {
      loadReports();
    }
  }, [isOpen, contentId]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const column = contentType === 'event' ? 'reported_event_id' : 'reported_job_id';
      const { data, error } = await supabase
        .from("user_reports")
        .select("*")
        .eq(column, contentId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error("Error loading reports:", error);
      toast({
        title: t("common.error"),
        description: "Errore nel caricamento delle segnalazioni",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateReportStatus = async (reportId: string, newStatus: string) => {
    setActionLoading(reportId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { error } = await supabase
        .from("user_reports")
        .update({
          status: newStatus,
          reviewed_by: session?.user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", reportId);

      if (error) throw error;

      toast({
        title: "Operazione completata",
        description: newStatus === "dismissed" ? "Segnalazione ignorata" : "Segnalazione risolta",
      });

      loadReports();
      onAction();
    } catch (error) {
      console.error("Error updating report:", error);
      toast({
        title: t("common.error"),
        description: "Errore nell'aggiornamento della segnalazione",
        variant: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleHideContent = async () => {
    setActionLoading("hide");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke("hide-content", {
        body: {
          contentType,
          contentId,
          reason: hideReason,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Contenuto nascosto",
        description: `${contentType === 'event' ? 'Evento' : 'Annuncio'} rimosso e proprietario notificato`,
      });

      setHideDialogOpen(false);
      setHideReason("");
      onAction();
      onClose();
    } catch (error: any) {
      console.error("Error hiding content:", error);
      toast({
        title: t("common.error"),
        description: error.message || "Errore nel nascondere il contenuto",
        variant: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const pendingReports = reports.filter(r => r.status === "pending");
  const resolvedReports = reports.filter(r => r.status !== "pending");

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-xl">
              <Flag className="w-5 h-5 text-destructive" />
              Segnalazioni per: {contentTitle}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              Nessuna segnalazione per questo contenuto
            </p>
          ) : (
            <div className="space-y-6">
              {/* Pending Reports */}
              {pendingReports.length > 0 && (
                <div>
                  <h3 className="font-heading font-bold text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Segnalazioni pendenti ({pendingReports.length})
                  </h3>
                  <div className="space-y-3">
                    {pendingReports.map((report) => (
                      <div 
                        key={report.id} 
                        className="border-[3px] border-dark p-4 bg-amber-50 shadow-[4px_4px_0px_0px] shadow-dark"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-bold text-amber-800">
                                {REPORT_REASONS[report.report_reason] || report.report_reason}
                              </span>
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(report.created_at), "dd/MM/yyyy HH:mm", { locale: it })}
                              </span>
                            </div>
                            {report.report_details && (
                              <p className="text-sm text-muted-foreground">
                                {report.report_details}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateReportStatus(report.id, "dismissed")}
                              disabled={actionLoading === report.id}
                              className="p-2 border-[2px] border-dark bg-muted hover:bg-muted/80 transition-colors"
                              title="Ignora"
                            >
                              {actionLoading === report.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => updateReportStatus(report.id, "resolved")}
                              disabled={actionLoading === report.id}
                              className="p-2 border-[2px] border-dark bg-green-100 hover:bg-green-200 transition-colors"
                              title="Risolto"
                            >
                              {actionLoading === report.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4 text-green-700" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resolved Reports */}
              {resolvedReports.length > 0 && (
                <div>
                  <h3 className="font-heading font-bold text-sm uppercase tracking-wide mb-3 text-muted-foreground">
                    Segnalazioni chiuse ({resolvedReports.length})
                  </h3>
                  <div className="space-y-2">
                    {resolvedReports.map((report) => (
                      <div 
                        key={report.id} 
                        className="border-[2px] border-dark/30 p-3 bg-muted/50"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {REPORT_REASONS[report.report_reason] || report.report_reason}
                          </span>
                          <span className={`text-xs px-2 py-0.5 font-bold uppercase ${
                            report.status === "resolved" 
                              ? "bg-green-100 text-green-800" 
                              : "bg-gray-100 text-gray-800"
                          }`}>
                            {report.status === "resolved" ? "Risolto" : "Ignorato"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hide Content Button */}
              {pendingReports.length > 0 && (
                <div className="border-t-[3px] border-dark pt-4">
                  <BrutalButton
                    variant="danger"
                    onClick={() => setHideDialogOpen(true)}
                    className="w-full"
                  >
                    <EyeOff className="w-4 h-4 mr-2" />
                    Nascondi {contentType === 'event' ? 'Evento' : 'Annuncio'}
                  </BrutalButton>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Questa azione rimuoverà il contenuto e invierà un'email al proprietario
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hide Content Confirmation Dialog */}
      <AlertDialog open={hideDialogOpen} onOpenChange={setHideDialogOpen}>
        <AlertDialogContent className="border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <EyeOff className="w-5 h-5 text-destructive" />
              Conferma rimozione contenuto
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stai per nascondere "{contentTitle}". Il proprietario riceverà un'email con il motivo della rimozione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <label className="font-heading text-sm font-bold uppercase tracking-wide mb-2 block">
              Motivo (opzionale)
            </label>
            <BrutalTextarea
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
              placeholder="Specifica il motivo della rimozione..."
              rows={3}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="border-[3px] border-dark">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHideContent}
              disabled={actionLoading === "hide"}
              className="bg-destructive text-destructive-foreground border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
            >
              {actionLoading === "hide" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Conferma Rimozione
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
